import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Link,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import * as React from 'react';

import { openDashboardAt } from '../auth/leaveDashboard';
import { sandboxHandoffTarget } from '../auth/sandboxHandoff';
import config from '../config';
import { sandboxSetupLine } from '../onboarding/sandboxProgress';
import AddSandboxAdminDialog from './AddSandboxAdminDialog';
import { DeveloperApiError, useDeveloperApi } from './api';
import { Field, ToneChip, describeError, formatTs, mono } from './components';
import { OUTBOUND_MODES, OUTBOX_CHANNELS, OUTBOX_REASONS, sandboxDataSource, sandboxStatus } from './labels';
import MirrorPanel from './MirrorPanel';
import QuotaPanel from './QuotaPanel';
import { SigninError } from './SigninError';
import type { SandboxCard as SandboxCardData, SandboxOutboxWindow, SandboxSigninRequest, SandboxSigninTargets } from './sandboxTypes';
import type { DeveloperMirror } from './types';

const ALLOW_HTTP = config.env === 'dev';
export { MFA_SETUP_PATH, SigninError } from './SigninError';

/** Sign in to the tenant's sandbox (developer.openapi.yaml developerSandboxSignin) and go there in this tab: the
 *  production session stays where it is (another origin), and Back returns here. Only the contract's sign-in page on
 *  another dashboard is followed. `busy` names the request in flight. */
export function useSandboxSignin(navigate: (url: string) => void = openDashboardAt) {
    const api = useDeveloperApi();
    const [busy, setBusy] = React.useState<string | null>(null);
    const [error, setError] = React.useState<unknown>(null);
    const open = React.useCallback(async (request: SandboxSigninRequest = {}, key = 'open') => {
        setBusy(key);
        setError(null);
        try {
            const answer = await api.sandboxSignin(request);
            const target = sandboxHandoffTarget(answer?.url, { allowHttp: ALLOW_HTTP });
            if (!target) {
                throw new DeveloperApiError(502, { message: 'The server answered with an unexpected sandbox address, so the dashboard did not follow it.' });
            }
            navigate(target);
            return true;
        } catch (e) {
            setError(e);
            return false;
        } finally {
            setBusy(null);
        }
    }, [api, navigate]);
    return { open, busy, error, setError };
}

const counts = (table: Record<string, number>, names: Record<string, string>) => {
    const entries = Object.entries(table || {}).sort((a, b) => b[1] - a[1]);
    return entries.length === 0 ? '—' : entries.map(([key, n]) => `${names[key] || key} ${n}`).join(' · ');
};

const OutboxWindow = ({ label, window }: { label: string; window: SandboxOutboxWindow }) => (
    <Box data-testid={`sandbox-outbox-${label}`}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {label}: {window.captured.toLocaleString()} captured
        </Typography>
        <Typography variant="body2" color="text.secondary">By channel: {counts(window.by_channel, OUTBOX_CHANNELS)}</Typography>
        <Typography variant="body2" color="text.secondary">By reason: {counts(window.by_reason, OUTBOX_REASONS)}</Typography>
    </Box>
);

/** "Open sandbox as…": the sandbox's users (developerSandboxSigninTargets), the default (its admin) first. */
const OpenAsDialog = ({ open, onClose, onPick, busy, signinError }: {
    open: boolean;
    onClose: () => void;
    onPick: (userId: number) => void;
    busy: boolean;
    signinError: unknown;
}) => {
    const api = useDeveloperApi();
    const [targets, setTargets] = React.useState<SandboxSigninTargets | null>(null);
    const [error, setError] = React.useState<unknown>(null);

    React.useEffect(() => {
        if (!open) {
            return;
        }
        setTargets(null);
        setError(null);
        api.sandboxSigninTargets().then(setTargets).catch(setError);
    }, [open, api]);

    return (
        <Dialog open={open} onClose={onClose} aria-labelledby="open-sandbox-as-title" fullWidth maxWidth="xs" sx={{ zIndex: 10002 }}>
            <DialogTitle id="open-sandbox-as-title">Open sandbox as…</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 1 }}>
                    Sign in to the sandbox as one of its users. The session lasts a few hours; open it again from here afterwards.
                </DialogContentText>
                {error ? <SigninError error={error} /> : null}
                {signinError ? <SigninError error={signinError} /> : null}
                {!targets && !error ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box> : null}
                {targets && targets.users.length === 0 ? <Typography variant="body2">The sandbox has no active users.</Typography> : null}
                {targets && targets.users.length > 0 ? (
                    <List dense data-testid="sandbox-targets">
                        {targets.users.map((user) => (
                            <ListItemButton key={user.id} disabled={busy} onClick={() => onPick(user.id)}>
                                <ListItemText
                                    primary={`${user.display_name || user.email}${user.is_default ? ' (sandbox admin)' : ''}`}
                                    secondary={user.email}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
};

/** Production's view of its sandbox (DSEC D3; plan §2 "the control plane"): status, data, quotas, what its outbox
 *  captured, its outbound mode; "Open sandbox", "Open sandbox as…" and "Open outbox" sign in to it (no outbox in
 *  production), "Resume" lifts its own suspension. A sandbox without an admin (`has_admin` false: "Open sandbox"
 *  and "Open outbox" would have nobody to sign in as) offers one "Add sandbox admin" in their place (DSEC D6);
 *  `onAdminAdded` reads the Overview again. `extra`: more fields from other features (D5's SMS state). The ESB
 *  fields: the mirror of production's webhooks switched from here (`onMirrorChange`, `canManageMirror` =
 *  `permissions.sandbox_mirror_manage`; MirrorPanel's rules and confirmation), whether production received a webhook
 *  from its booking system yet, and the set-up of a sandbox asked for at sign-up. */
const SandboxCard = ({
    card,
    canSignIn,
    onResumed,
    onAdminAdded,
    canManageMirror = false,
    onMirrorChange,
    extra,
    navigate,
}: {
    card: SandboxCardData;
    canSignIn: boolean;
    onResumed?: () => void;
    onAdminAdded?: () => void;
    canManageMirror?: boolean;
    onMirrorChange?: (enabled: boolean) => Promise<DeveloperMirror>;
    extra?: React.ReactNode;
    navigate?: (url: string) => void;
}) => {
    const api = useDeveloperApi();
    const signin = useSandboxSignin(navigate);
    const [asOpen, setAsOpen] = React.useState(false);
    const [addOpen, setAddOpen] = React.useState(false);
    const [resuming, setResuming] = React.useState(false);
    const [resumeError, setResumeError] = React.useState<unknown>(null);

    const status = sandboxStatus(card.status);
    const source = sandboxDataSource(card.data_source);
    const mode = OUTBOUND_MODES[card.outbound.mode];
    const suspended = card.suspension.suspended;
    const blocked = !canSignIn || !card.signin_open || signin.busy !== null;
    const noPermission = 'Needs the permission “Developer: sign in to the sandbox” (ask an administrator to add you to a Developers group).';
    const why = !canSignIn ? noPermission : !card.signin_open ? 'The sandbox is suspended: resume it first.' : '';

    const resume = async () => {
        setResuming(true);
        setResumeError(null);
        try {
            await api.resumeSandbox();
            onResumed?.();
        } catch (e) {
            setResumeError(e);
        } finally {
            setResuming(false);
        }
    };

    const action = (label: string, key: string, onClick: () => void, variant: 'contained' | 'outlined' = 'outlined') => (
        <Tooltip title={why}>
            <span>
                <Button
                    size="small"
                    variant={variant}
                    disabled={blocked}
                    onClick={onClick}
                    startIcon={signin.busy === key ? <CircularProgress size={14} /> : undefined}
                >
                    {label}
                </Button>
            </span>
        </Tooltip>
    );

    return (
        <Paper variant="outlined" sx={{ p: 2 }} data-testid="sandbox-card">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5, rowGap: 1 }}>
                <ScienceOutlinedIcon color="warning" fontSize="small" />
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>Sandbox</Typography>
                <Typography variant="body2" color="text.secondary">{card.tenant.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={mono}>{card.tenant.code}</Typography>
                <span data-testid="sandbox-status"><ToneChip {...status} /></span>
                <span data-testid="sandbox-data-source"><ToneChip {...source} /></span>
            </Stack>

            {suspended && (
                <Alert
                    severity={card.suspension.locked ? 'error' : 'warning'}
                    sx={{ mb: 1.5 }}
                    data-testid="sandbox-suspended"
                    action={!card.suspension.locked && canSignIn ? (
                        <Button color="inherit" size="small" onClick={resume} disabled={resuming} startIcon={resuming ? <CircularProgress size={14} /> : undefined}>
                            Resume
                        </Button>
                    ) : undefined}
                >
                    {card.suspension.locked
                        ? 'Curated Planet suspended this sandbox; only they can resume it.'
                        : 'The sandbox is suspended: it imports nothing, sends nothing and refuses writes. Resume it to sign in.'}
                    {card.suspension.at ? ` Since ${formatTs(card.suspension.at)}.` : ''}
                    {card.suspension.reason ? ` Reason: ${card.suspension.reason}` : ''}
                </Alert>
            )}
            {resumeError ? <Alert severity="error" sx={{ mb: 1.5 }}>{describeError(resumeError)}</Alert> : null}

            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Stack spacing={1.5}>
                        <Field label="Outbound messages">
                            <span data-testid="sandbox-outbound">
                                {mode ? mode.label : card.outbound.mode}
                                {card.outbound.mode === 'allowlist'
                                    ? `, ${card.outbound.allowlist_size} ${card.outbound.allowlist_size === 1 ? 'entry' : 'entries'} on the allowlist`
                                    : ''}
                            </span>
                        </Field>
                        <Field label="Last captured message">
                            {card.outbox ? formatTs(card.outbox.last_captured_at) : '—'}
                        </Field>
                        <Field label="Dashboard">
                            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{card.dashboard_url}</Typography>
                        </Field>
                        {extra}
                    </Stack>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Field label="Outbox (captured, not delivered)">
                        {card.outbox ? (
                            <Stack spacing={1} data-testid="sandbox-outbox">
                                <OutboxWindow label="Last 24 hours" window={card.outbox.last_24h} />
                                <OutboxWindow label="Last 7 days" window={card.outbox.last_7d} />
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary">The outbox counts are not available right now.</Typography>
                        )}
                    </Field>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Field label="Quotas">
                        <QuotaPanel quotas={card.quotas} />
                    </Field>
                </Grid>
            </Grid>

            {(card.mirror || card.webhooks_received != null || card.setup) && (
                <Grid container spacing={2} sx={{ mt: 0 }} data-testid="sandbox-card-mirror">
                    {card.mirror && onMirrorChange && (
                        <Grid item xs={12} md={8}>
                            <Field label="Mirror of production's webhooks">
                                <MirrorPanel
                                    mirror={card.mirror}
                                    canManage={canManageMirror}
                                    outboundMode={card.outbound.mode}
                                    onChange={onMirrorChange}
                                    where="production"
                                    suspended={suspended}
                                />
                            </Field>
                        </Grid>
                    )}
                    <Grid item xs={12} md={4}>
                        <Stack spacing={1.5}>
                            {card.webhooks_received != null && (
                                <Field label="Webhooks from your booking system">
                                    <span data-testid="sandbox-webhooks-received">{card.webhooks_received ? 'Received' : 'Not yet'}</span>
                                </Field>
                            )}
                            {card.setup && (
                                <Field label="Set-up">
                                    <span data-testid="sandbox-setup-line">{sandboxSetupLine(card.setup)}</span>
                                </Field>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            )}

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2, rowGap: 1 }}>
                {/* Only an explicit false: a backend older than this dashboard sends no has_admin (deploy order). */}
                {card.has_admin !== false ? action('Open sandbox', 'open', () => signin.open({ landing: 'overview' }, 'open'), 'contained') : (
                    // One button in place of "Open sandbox" and "Open outbox", which both sign in as the admin.
                    // Allowed while suspended too: adding a user sends nothing (the sign-in waits for Resume).
                    <Tooltip title={canSignIn ? 'The sandbox has no administrator for “Open sandbox” or “Open outbox” to sign in as.' : noPermission}>
                        <span>
                            <Button size="small" variant="contained" disabled={!canSignIn} onClick={() => setAddOpen(true)}>
                                Add sandbox admin
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {action('Open sandbox as…', 'as', () => setAsOpen(true))}
                {card.has_admin !== false && action('Open outbox', 'outbox', () => signin.open({ landing: 'outbox' }, 'outbox'))}
            </Stack>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                Opens the sandbox's dashboard in this tab, signed in for a few hours; you stay signed in here.
                {' '}It needs a sign-in with two-step verification or single sign-on.
                {' '}<Link href={card.dashboard_url} target="_blank" rel="noopener noreferrer">
                    Sandbox sign-in page<OpenInNewIcon sx={{ fontSize: 12, ml: 0.25, verticalAlign: 'middle' }} />
                </Link>
            </Typography>
            <SigninError error={signin.error} />

            <AddSandboxAdminDialog
                open={addOpen}
                sandboxName={card.tenant.name || card.tenant.code}
                onClose={() => setAddOpen(false)}
                onAdded={() => {
                    setAddOpen(false);
                    onAdminAdded?.();
                }}
            />
            <OpenAsDialog
                open={asOpen}
                busy={signin.busy !== null}
                signinError={signin.error}
                onClose={() => setAsOpen(false)}
                onPick={(userId) => signin.open({ user_id: userId, landing: 'overview' }, 'as')}
            />
        </Paper>
    );
};

export default SandboxCard;
